import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useDelayedAccessDenied } from '@/hooks/useDelayedAccessDenied';
import { useTranslation } from 'react-i18next';

interface ModuleGuardProps {
  /** enabled_modules key required to access the wrapped route (e.g. 'app_studio'). */
  module: string;
  children: React.ReactNode;
}

/**
 * Route-level defense-in-depth gate for per-tenant modules (Task 1.14).
 *
 * The sidebar already hides links for modules a tenant doesn't have; this
 * blocks direct-URL access too. Shows a notice with a link back to /dashboard when
 * the logged-in tenant's enabled_modules does not include `module`. Platform admins always
 * pass.
 *
 * Source of truth is `useAuth().tenantData` — the logged-in user's own tenant
 * row (loaded by AuthContext for tenant_admins). We deliberately do NOT use
 * useRuntimeAccess here: its config depends on the platform-admin tenant-switcher
 * (selectedTenant), which is null for ordinary tenant_admins.
 *
 * Wrap INSIDE ProtectedRoute so auth + role are already satisfied. The grace
 * period prevents a redirect/flash while auth + tenantData are still loading;
 * fail-closed once the tenant context is known.
 */
export function ModuleGuard({ module, children }: ModuleGuardProps) {
  const { tenantData, userProfile, loading } = useAuth();
  const { t } = useTranslation('common');

  const isPlatformAdmin = userProfile?.role === 'platform_admin';
  const hasModule = isPlatformAdmin || (tenantData?.enabled_modules?.includes(module) ?? false);

  // Still resolving while auth loads, or while a tenant_admin's tenantData
  // hasn't arrived yet — don't deny/redirect during that window.
  const stillResolving = loading || (!isPlatformAdmin && tenantData === null);

  const { showDenied, isInGracePeriod } = useDelayedAccessDenied(
    !hasModule,
    stillResolving,
    1000,
  );

  if (stillResolving || isInGracePeriod) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('validatingAccess')}</p>
        </div>
      </div>
    );
  }

  if (showDenied) {
    return (
      <div role="alert" className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg font-medium">{t('moduleUnavailable')}</p>
          <Button asChild variant="outline">
            <Link to="/dashboard">{t('backToDashboard')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
